import React, { useRef } from 'react';

export function usePanAndZoom<
  ContainerType extends HTMLElement = HTMLElement,
  ChildType extends HTMLElement | SVGElement = HTMLElement,
>(
  effectiveMargin: {
    left: number;
    top: number;
    right: number;
    bottom: number;
  } = { left: 0, bottom: 0, right: 0, top: 0 },
  blockZoom = true,
) {
  const boxRef = useRef<ContainerType>(null);
  const elementRef = useRef<ChildType>(null);
  const [isPanning, setIsPanning] = React.useState(false);
  const [panningPosition, setPanningPosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (blockZoom) return;
    const container = boxRef.current;
    const innerElement = elementRef.current;
    const handlePanStart = (ev: MouseEvent) => {
      ev.preventDefault();
      setIsPanning(true);
      setPanningPosition({ x: ev.clientX, y: ev.clientY });
    };
    const handlePanEnd = (ev: MouseEvent) => {
      ev.preventDefault();

      setIsPanning(false);
    };

    const handlePan = (ev: MouseEvent) => {
      if (!innerElement || !container) {
        return;
      }

      if (isPanning) {
        ev.preventDefault();
        ev.stopPropagation();

        const dx = ev.clientX - panningPosition.x;
        const dy = ev.clientY - panningPosition.y;

        container.scrollLeft -= dx;
        container.scrollTop -= dy;

        setPanningPosition({ x: ev.clientX, y: ev.clientY });
      }
    };

    if (container) {
      container.addEventListener('mousedown', handlePanStart);
      container.addEventListener('mouseup', handlePanEnd);
      container.addEventListener('mouseleave', handlePanEnd);
      container.addEventListener('mousemove', handlePan);
      return () => {
        container.removeEventListener('mousedown', handlePanStart);
        container.removeEventListener('mousemove', handlePan);
        container.removeEventListener('mouseup', handlePanEnd);
        container.removeEventListener('mouseleave', handlePanEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPanning, panningPosition]);

  React.useEffect(() => {
    if (blockZoom) return;

    const container = boxRef.current;
    const innerElement = elementRef.current;
    let newScale = 1;
    const handleZoom = (ev: WheelEvent) => {
      ev.preventDefault();

      const newDelta = ev.deltaY;
      const scaleIncrement = 0.1;

      if (newDelta < 0) {
        if (newScale + scaleIncrement >= 2) {
          return;
        }
        newScale += scaleIncrement;
      } else {
        if (newScale - scaleIncrement <= 0.9) {
          return;
        }
        newScale -= scaleIncrement;
      }
      if (!innerElement || !container) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const cursorX = ev.clientX - containerRect.left - effectiveMargin.left;
      const cursorY = ev.clientY - containerRect.top - effectiveMargin.top;

      const cursorXFromCenter = cursorX - containerRect.width / 2;
      const cursorYFromCenter = cursorY - containerRect.height / 2;

      if (newScale <= 1) {
        if (boxRef.current) {
          boxRef.current.style.overflow = 'hidden';
        }
      } else {
        if (boxRef.current) {
          boxRef.current.style.overflow = 'hidden';
        }
      }
      innerElement.style.transform = `scale(${newScale})`;
      innerElement.style.transformOrigin = '0px 0px';

      if (newDelta < 0) {
        container.scrollLeft += cursorXFromCenter / (newScale * 2);
        container.scrollTop += cursorYFromCenter / (newScale * 2);
      } else {
        container.scrollLeft -= cursorXFromCenter / (newScale * 2);
        container.scrollTop -= cursorYFromCenter / (newScale * 2);
      }
    };
    if (container) {
      container.addEventListener('wheel', handleZoom);
      return () => {
        container.removeEventListener('wheel', handleZoom);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    boxRef,
    elementRef,
  };
}
